/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-get, ember/no-observers, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import { enumOption } from '@linc-technologies/linced-common/helpers/enum-option';
import GradingMethod from '@linc-technologies/linced-common/enums/grading-method';
import { classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';

@classNames('flex', 'layout-row')
export default class GradeSchedulePicker extends Component {
  @service
  session;

  @service
  store;

  GradingMethod = GradingMethod;

  // The form to register the grade schedule field with, so it counts towards
  // the form's validity.
  parentComponent = null;

  dialogOpen = false;

  // Not @tracked: set() does not write a @tracked field back to the parent.
  // Use set() to change it.
  editingSchedule = null;

  @tracked
  label = '';

  @action
  setLabel(value) {
    this.label = value;
  }

  @tracked
  description = '';

  @action
  setDescription(value) {
    this.description = value;
  }

  @tracked
  isShared = true;

  @action
  setIsShared(value) {
    this.isShared = value;
  }

  @tracked
  awardMethod = null;

  @action
  setAwardMethod(value) {
    this.awardMethod = value;
  }

  // awardMethod = GradeMethod.NUMERIC
  @tracked
  gradeFrom = 0;

  @action
  setGradeFrom(value) {
    this.gradeFrom = value;
  }

  @tracked
  gradeTo = 0;

  @action
  setGradeTo(value) {
    this.gradeTo = value;
  }

  @tracked
  gradeStep = 0;

  @action
  setGradeStep(value) {
    this.gradeStep = value;
  }

  // awardMethod = GradeMethod.SELECT || GradeMethod.MULTIPLE_SELECT
  @tracked
  selectOptions = [{}];

  @tracked
  errorMessage = '';

  @observes('dialogOpen')
  _startEditing() {
    let schedule = this.editingSchedule;
    if (this.dialogOpen && schedule) {
      // Dialog has been triggered with a schedule to edit; reset and populate the form.
      let awardMethod = schedule.get('awardMethod');
      this.resetForm();
      this.set('editingSchedule', schedule);

      this.label = schedule.get('label');
      this.description = schedule.get('description');
      this.isShared = schedule.get('isShared');
      this.awardMethod = enumOption([GradingMethod, awardMethod]);

      switch (awardMethod) {
        case GradingMethod.NUMERIC:
          this.gradeFrom = schedule.get('numeric.range.start');
          this.gradeTo = schedule.get('numeric.range.end');
          this.gradeStep = schedule.get('numeric.defaultNumericStepping');
          break;

        case GradingMethod.SELECT:
        case GradingMethod.MULTIPLE_SELECT:
        case GradingMethod.ICON_SELECT:
          this.selectOptions = schedule.get('select.values');
          break;
      }
    }
  }

  onChange() {}

  @action
  openDialog() {
    this.resetForm();
    this.set('dialogOpen', true);
  }

  @action
  closeDialog() {
    // Stop a save that runs, so that it does not change the next dialog.
    this.saveTask.cancelAll();
    this.set('dialogOpen', false);
  }

  // dropTask ignores a second submit while a save runs.
  saveTask = dropTask(async () => {
    let schedule = this.editingSchedule;
    let store = this.store;
    let isNew = false;

    // Create a new Grade Schedule if needed
    if (isEmpty(schedule)) {
      isNew = true;
      schedule = store.createRecord('assessments/grade-schedule', {});
    }

    // Set properties from form
    let data = this.getProperties('label', 'description', 'isShared');
    let awardMethod = this.get('awardMethod.value');
    switch (awardMethod) {
      case GradingMethod.NUMERIC:
        Object.assign(data, {
          awardMethod,
          numeric: {
            range: {
              start: this.gradeFrom,
              end: this.gradeTo,
            },
            defaultNumericStepping: this.gradeStep,
          },
        });
        break;
      case GradingMethod.SELECT:
      case GradingMethod.MULTIPLE_SELECT:
      case GradingMethod.ICON_SELECT:
        Object.assign(data, {
          awardMethod,
          select: {
            values: this.selectOptions,
          },
        });
        break;
    }

    schedule.setProperties(data);

    try {
      await schedule.save();
      if (isNew && this.onChange) {
        // Select the newly-created schedule.
        this.onChange(schedule);
      }
      // closeDialog cancels this task, so call it last.
      this.closeDialog();
    } catch (err) {
      this.errorMessage = err;
    }
  });

  @action
  moveOption({ sourceIndex, targetIndex }) {
    if (sourceIndex === targetIndex) {
      return;
    }

    let options = [...this.selectOptions];
    let [draggedItem] = options.splice(sourceIndex, 1);
    options.splice(targetIndex, 0, draggedItem);
    this.selectOptions = options;
  }

  @action
  addOption() {
    this.selectOptions = [...this.selectOptions, {}];
  }

  @action
  removeOption(option) {
    this.selectOptions = this.selectOptions.filter((o) => o !== option);
  }

  resetForm() {
    this.errorMessage = '';
    this.label = '';
    this.description = '';
    this.isShared = true;
    this.set('editingSchedule', null);
    this.awardMethod = enumOption([GradingMethod, GradingMethod.NUMERIC]);
    this.gradeFrom = 0;
    this.gradeTo = 100;
    this.gradeStep = 1;
    this.selectOptions = [{}];
  }
}
